/**
 * Ventana «Reemplazar fuente» del Creador en 3 pasos: elegir la hoja nueva (enlace, Drive o
 * pestaña), emparejar las columnas que usa el reporte con las nuevas (con sugerencias) y
 * confirmar. Solo lee del backend; quien la abre aplica el resultado con applyColumnMapping.
 * En modo «widget» empieza en el paso 2 con las columnas de la fuente destino.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ink } from '../studioUi';
import DriveSpreadsheetPicker from './DriveSpreadsheetPicker';
import { fetchWorksheets, fetchSheetColumns } from './sourceReplaceApi';
import { normalizeColumnName, suggestColumnMapping } from './sourceReplace';

const STEPS = ['1 · Nueva hoja', '2 · Columnas', '3 · Confirmar'];

function usageText(list = []) {
    return list.map(item => `${item.label}${item.pageName ? ` (${item.pageName})` : ''}`).join(', ');
}

const ReplaceSourceDialog = ({
    open,
    mode = 'source',
    source,
    targetSource = null,
    initialInput = '',
    initialSheet = '',
    usedColumns = [],
    usage = {},
    onClose,
    onApply,
}) => {
    const widgetMode = mode === 'widget';
    const [step, setStep] = useState(widgetMode ? 1 : 0);
    const [input, setInput] = useState('');
    const [file, setFile] = useState({ spreadsheet_id: '', sheets: [] });
    const [sheetName, setSheetName] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [newColumns, setNewColumns] = useState([]);
    const [mapping, setMapping] = useState({});
    const [added, setAdded] = useState([]);
    const [showExact, setShowExact] = useState(false);
    const [driveOpen, setDriveOpen] = useState(false);

    const loadSheets = async (value) => {
        setBusy(true);
        setError('');
        try {
            const res = await fetchWorksheets(value);
            setFile(res);
            const keep = res.sheets.find(item => item.title === initialSheet)
                || res.sheets.find(item => item.title === source?.sheet_name);
            setSheetName((keep || res.sheets[0]).title);
        } catch (err) {
            setError(err.message);
            setFile({ spreadsheet_id: '', sheets: [] });
        } finally {
            setBusy(false);
        }
    };

    const loadColumns = async (target) => {
        setBusy(true);
        setError('');
        try {
            const cols = await fetchSheetColumns(target);
            const result = suggestColumnMapping(usedColumns, cols);
            setNewColumns(cols);
            setMapping(Object.fromEntries(result.rows.map(row => [row.from, row.to || ''])));
            setAdded(result.added);
            setStep(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        setError('');
        setShowExact(false);
        if (widgetMode) {
            setStep(1);
            if (targetSource) loadColumns({ spreadsheet_id: targetSource.spreadsheet_id, sheet_name: targetSource.sheet_name });
            return;
        }
        const start = initialInput || source?.input || source?.spreadsheet_id || '';
        setStep(0);
        setInput(start);
        setFile({ spreadsheet_id: '', sheets: [] });
        if (start) loadSheets(start);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, source?.id, targetSource?.id, initialInput, initialSheet]);

    const rows = useMemo(() => usedColumns.map((col) => {
        const to = mapping[col.name] || '';
        const status = !to ? 'missing' : (normalizeColumnName(to) === normalizeColumnName(col.name) ? 'exact' : 'suggested');
        return { from: col.name, to, status };
    }), [usedColumns, mapping]);
    const counts = rows.reduce((acc, row) => ({ ...acc, [row.status]: acc[row.status] + 1 }), { exact: 0, suggested: 0, missing: 0 });
    const visibleRows = showExact ? rows : rows.filter(row => row.status !== 'exact');
    const renames = rows.filter(row => row.to && row.to !== row.from);
    const missing = rows.filter(row => !row.to).map(row => row.from);
    const takenBy = useMemo(() => {
        const map = {};
        rows.forEach((row) => { if (row.to) map[row.to] = row.from; });
        return map;
    }, [rows]);

    if (!open) return null;

    const apply = () => {
        onApply({
            spreadsheet_id: widgetMode ? targetSource?.spreadsheet_id : file.spreadsheet_id,
            sheet_name: widgetMode ? targetSource?.sheet_name : sheetName,
            input: widgetMode ? (targetSource?.input || '') : input,
            mapping: Object.fromEntries(renames.map(row => [row.from, row.to])),
            missing,
        });
    };

    const statusIcon = { exact: '✓', suggested: '≈', missing: '✕' };
    const statusColor = { exact: '#137333', suggested: '#b06000', missing: ink.danger };
    const destination = widgetMode ? targetSource : { label: source?.label, sheet_name: sheetName, spreadsheet_id: file.spreadsheet_id };

    return (
        <div style={ui.overlay} role="dialog" aria-modal="true" aria-label="Reemplazar fuente">
            <div style={ui.sheet}>
                <div style={ui.head}>
                    <div>
                        <span style={ui.kicker}>{widgetMode ? 'Cambiar fuente del elemento' : 'Reemplazar fuente'}</span>
                        <h3 style={ui.title}>{source?.label || 'Fuente'}</h3>
                    </div>
                    <button type="button" style={ui.close} onClick={onClose} aria-label="Cerrar">×</button>
                </div>

                <div style={ui.steps}>
                    {STEPS.map((label, idx) => (widgetMode && idx === 0 ? null : (
                        <span key={label} data-step-active={idx === step ? 'true' : 'false'} style={idx === step ? ui.stepOn : ui.step}>{label}</span>
                    )))}
                </div>

                <div style={ui.compare}>
                    <div style={ui.box}>
                        <small style={ui.boxLabel}>Actual</small>
                        <strong>{source?.label || source?.sheet_name}</strong>
                        <span style={ui.boxSub}>pestaña «{source?.sheet_name || '—'}»</span>
                    </div>
                    <span style={ui.arrow}>→</span>
                    <div style={{ ...ui.box, borderColor: ink.accent }}>
                        <small style={ui.boxLabel}>Nueva</small>
                        <strong>{destination?.label || destination?.spreadsheet_id || 'Sin elegir'}</strong>
                        <span style={ui.boxSub}>pestaña «{destination?.sheet_name || '—'}»</span>
                    </div>
                </div>

                <div style={ui.body}>
                    {error && <div style={ui.error}>{error}</div>}

                    {step === 0 && (
                        <div style={ui.stack}>
                            <label style={ui.label} htmlFor="replace-source-input">Enlace o ID de Google Sheets</label>
                            <div style={ui.inline}>
                                <input
                                    id="replace-source-input"
                                    style={ui.input}
                                    value={input}
                                    onChange={e => setInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); loadSheets(input); } }}
                                    placeholder="https://docs.google.com/spreadsheets/d/…"
                                />
                                <button type="button" style={ui.ghost} onClick={() => loadSheets(input)} disabled={busy || !input.trim()}>Leer archivo</button>
                                <button type="button" style={ui.ghost} onClick={() => setDriveOpen(true)}>Drive</button>
                            </div>
                            <label style={ui.label} htmlFor="replace-source-sheet">Pestaña</label>
                            <select id="replace-source-sheet" style={ui.input} value={sheetName} onChange={e => setSheetName(e.target.value)} disabled={!file.sheets.length}>
                                {!file.sheets.length && <option value="">{busy ? 'Leyendo…' : 'Lee el archivo primero'}</option>}
                                {file.sheets.map(item => <option key={`${item.gid}-${item.title}`} value={item.title}>{item.title}</option>)}
                            </select>
                            <p style={ui.hint}>Nada cambia en el reporte hasta que confirmes en el paso 3.</p>
                        </div>
                    )}

                    {step === 1 && (
                        <div style={ui.stack}>
                            <div style={ui.chips}>
                                <span style={{ ...ui.chip, backgroundColor: '#e6f4ea', color: '#137333' }}>{`${counts.exact} coinciden`}</span>
                                <span style={{ ...ui.chip, backgroundColor: '#fef7e0', color: '#b06000' }}>{`${counts.suggested} sugeridas`}</span>
                                <span style={{ ...ui.chip, backgroundColor: ink.dangerSoft, color: ink.danger }}>{`${counts.missing} sin equivalente`}</span>
                                <span style={{ ...ui.chip, backgroundColor: ink.wash, color: ink.muted }} title={added.join(', ')}>{`${added.length} nuevas`}</span>
                            </div>
                            <div style={ui.table}>
                                <div style={{ ...ui.row, ...ui.rowHead }}>
                                    <span>Columna actual</span><span /><span>Columna nueva</span><span>Se usa en</span>
                                </div>
                                {visibleRows.map(row => (
                                    <div key={row.from} style={ui.row}>
                                        <span style={ui.cellName}>{row.from}</span>
                                        <span style={{ color: statusColor[row.status], fontWeight: 700 }} aria-hidden="true">{statusIcon[row.status]}</span>
                                        <select
                                            aria-label={`Columna nueva para ${row.from}`}
                                            style={ui.input}
                                            value={row.to}
                                            onChange={e => setMapping(prev => ({ ...prev, [row.from]: e.target.value }))}
                                        >
                                            <option value="">Sin equivalente</option>
                                            {newColumns.map(col => (
                                                <option key={col.name} value={col.name} disabled={Boolean(takenBy[col.name] && takenBy[col.name] !== row.from)}>
                                                    {col.name}{row.status === 'suggested' && row.to === col.name && row.to !== row.from ? ' (sugerida)' : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <span style={ui.cellUsage} title={usageText(usage[row.from])}>
                                            {(usage[row.from] || []).length ? `${usage[row.from].length} ${usage[row.from].length === 1 ? 'uso' : 'usos'}` : '—'}
                                        </span>
                                    </div>
                                ))}
                                {!visibleRows.length && <p style={ui.hint}>Todas las columnas coinciden.</p>}
                            </div>
                            {counts.exact > 0 && (
                                <button type="button" style={ui.link} onClick={() => setShowExact(v => !v)}>
                                    {showExact ? 'Ocultar las que coinciden' : `Mostrar las ${counts.exact} que coinciden`}
                                </button>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div style={ui.stack}>
                            <h4 style={ui.section}>Se renombrarán en todo el reporte</h4>
                            {renames.length ? (
                                <ul style={ui.list}>
                                    {renames.map(row => <li key={row.from}>{row.from} → <strong>{row.to}</strong></li>)}
                                </ul>
                            ) : <p style={ui.hint}>Ninguna columna cambia de nombre.</p>}
                            <h4 style={ui.section}>Quedarán como campo faltante</h4>
                            <ul style={ui.list} data-testid="missing-list">
                                {missing.length ? missing.map(name => (
                                    <li key={name}><strong>{name}</strong>{usage[name]?.length ? ` — ${usageText(usage[name])}` : ''}</li>
                                )) : <li>Ninguno.</li>}
                            </ul>
                            <p style={ui.hint}>Los títulos visibles se conservan. Puedes deshacer el reemplazo con «Deshacer».</p>
                        </div>
                    )}
                </div>

                <div style={ui.foot}>
                    {step > (widgetMode ? 1 : 0)
                        ? <button type="button" style={ui.ghost} onClick={() => setStep(s => s - 1)}>‹ Atrás</button>
                        : <button type="button" style={ui.ghost} onClick={onClose}>Cancelar</button>}
                    {step === 0 && (
                        <button
                            type="button"
                            style={ui.primary}
                            disabled={busy || !file.spreadsheet_id || !sheetName}
                            onClick={() => loadColumns({ spreadsheet_id: file.spreadsheet_id, sheet_name: sheetName })}
                        >
                            {busy ? 'Leyendo…' : 'Siguiente ›'}
                        </button>
                    )}
                    {step === 1 && <button type="button" style={ui.primary} onClick={() => setStep(2)} disabled={busy}>Revisar y aplicar ›</button>}
                    {step === 2 && <button type="button" style={ui.primary} onClick={apply}>Aplicar reemplazo</button>}
                </div>
            </div>
            <DriveSpreadsheetPicker
                open={driveOpen}
                onClose={() => setDriveOpen(false)}
                onSelect={(picked) => {
                    setDriveOpen(false);
                    if (!picked?.id) return;
                    const url = picked.url || `https://docs.google.com/spreadsheets/d/${picked.id}`;
                    setInput(url);
                    loadSheets(url);
                }}
                usedIds={[]}
            />
        </div>
    );
};

const pill = {
    appearance: 'none',
    padding: '8px 16px',
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: 'solid',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
};

const ui = {
    overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(28, 29, 33, 0.5)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
    sheet: { width: 'min(760px, 100%)', maxHeight: 'min(90vh, 860px)', backgroundColor: ink.surface, borderRadius: 18, display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 28px 80px rgba(28, 29, 33, 0.28)' },
    head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 18px 8px' },
    kicker: { fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: ink.accent },
    title: { margin: '4px 0 0', fontSize: '1.15rem', color: ink.text },
    close: { appearance: 'none', borderWidth: 0, backgroundColor: 'transparent', fontSize: '1.4rem', lineHeight: 1, color: ink.muted, cursor: 'pointer' },
    steps: { display: 'flex', gap: 6, padding: '0 18px 10px', flexWrap: 'wrap' },
    step: { padding: '4px 10px', borderRadius: 999, backgroundColor: ink.wash, color: ink.muted, fontSize: '0.75rem' },
    stepOn: { padding: '4px 10px', borderRadius: 999, backgroundColor: ink.accent, color: '#fff', fontSize: '0.75rem', fontWeight: 600 },
    compare: { display: 'flex', alignItems: 'stretch', gap: 10, padding: '0 18px 12px' },
    box: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2, padding: '8px 12px', borderRadius: 12, borderWidth: 1, borderStyle: 'solid', borderColor: ink.lineStrong, fontSize: '0.85rem', color: ink.text },
    boxLabel: { fontSize: '0.68rem', color: ink.faint, textTransform: 'uppercase', letterSpacing: '0.06em' },
    boxSub: { fontSize: '0.75rem', color: ink.muted },
    arrow: { alignSelf: 'center', color: ink.muted },
    body: { padding: '4px 18px 12px', overflowY: 'auto', flex: 1, minHeight: 0 },
    stack: { display: 'flex', flexDirection: 'column', gap: 8 },
    inline: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    label: { fontSize: '0.75rem', fontWeight: 600, color: ink.muted },
    input: { flex: '1 1 200px', minWidth: 0, padding: '7px 10px', borderRadius: 10, borderWidth: 1, borderStyle: 'solid', borderColor: ink.lineStrong, fontSize: '0.85rem', backgroundColor: ink.surface, color: ink.text },
    hint: { margin: 0, fontSize: '0.75rem', color: ink.muted },
    error: { padding: '8px 12px', borderRadius: 10, backgroundColor: ink.dangerSoft, color: ink.danger, fontSize: '0.82rem', marginBottom: 8 },
    chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    chip: { padding: '3px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600 },
    table: { display: 'flex', flexDirection: 'column', borderWidth: 1, borderStyle: 'solid', borderColor: ink.line, borderRadius: 12, overflow: 'hidden' },
    row: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) 20px minmax(0, 1.4fr) minmax(0, 0.7fr)', gap: 8, alignItems: 'center', padding: '6px 10px', borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: ink.line, fontSize: '0.85rem' },
    rowHead: { backgroundColor: ink.accentSoft, color: ink.muted, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 },
    cellName: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: ink.text },
    cellUsage: { fontSize: '0.78rem', color: ink.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    link: { appearance: 'none', borderWidth: 0, backgroundColor: 'transparent', color: ink.accent, fontSize: '0.78rem', cursor: 'pointer', alignSelf: 'flex-start', padding: 0 },
    section: { margin: '6px 0 0', fontSize: '0.85rem', color: ink.text },
    list: { margin: 0, paddingLeft: 18, fontSize: '0.85rem', color: ink.text, display: 'flex', flexDirection: 'column', gap: 4 },
    foot: { display: 'flex', justifyContent: 'space-between', gap: 8, padding: '12px 18px', borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: ink.line },
    ghost: { ...pill, borderColor: ink.lineStrong, backgroundColor: ink.surface, color: ink.accentText },
    primary: { ...pill, borderColor: ink.accent, backgroundColor: ink.accent, color: '#fff' },
};

export default ReplaceSourceDialog;
